<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const schema = defineSchema({
	articlesTitle: $.text('Articles Title', { default: 'Articles by Author' }),
	browseAuthorsTitle: $.text('Browse Authors Title', { default: 'Browse Authors' }),
	allAuthorsLink: $.text('All Authors Link', { default: '/authors' }),
})
export const BlockAuthorPage = defineBlock({
	thumbnail: '/block-thumbs/BlockAuthorPage.svg',
	category: 'Content',
	order: 130,
	name: 'BlockAuthorPage',
	label: 'Author Page',
	icon: 'person',
	schema,
})
</script>
<script setup lang="ts">
import { useSeo } from '@bagelink/blox'
import { useRoute } from 'vue-router'
import { pageSeo } from '@/utils/seo'
import { useBoundPageItem } from '@/composables/useBoundPageItem'

import { computed } from 'vue'
import BlockArticleList from '@/components/blocks/BlockArticleList.vue'
import BlockAuthorList from '@/components/blocks/BlockAuthorList.vue'
import BlockAuthorProfile from '@/components/blocks/BlockAuthorProfile.vue'
import { useBoundPageModel } from '@/composables/useBoundPageItem'
import { formatRecordDate, useAuthorDates } from '@/composables/useAuthorDates'



interface Book {
	title: string
	image: string
	link?: string
}

interface Author {
	name: string
	title: string
	institution: string
	image: string
	link: string
}

interface ArticleAuthor {
	name: string
	title: string
	image: string
}

interface Article {
	title: string
	authors: ArticleAuthor[]
	image: string
	link: string
	subtitle?: string
}

defineProps<{
	articlesTitle?: string
	browseAuthorsTitle?: string
	allAuthorsLink?: string
}>()

const previewRecord = {
	_id: 'preview-author',
	name: 'Sample Author',
	title: 'Prof.',
	institution: 'Sample University',
	image: '/webclip.png',
	bio: '<p>This biography previews a datastore-bound author page.</p>',
	_previewBooks: [{ title: 'Sample Book', image: '/og.jpg', link: '' }],
	_previewArticles: [
		{
			title: 'Sample Article',
			subtitle: 'An introduction to this author’s scholarship',
			image: '/og.jpg',
			link: '/article/sample',
			authors: [{ name: 'Sample Author', title: 'Biblical Scholar', image: '/webclip.png' }],
		},
	],
	_previewAuthors: [
		{
			name: 'Another Author',
			title: 'Prof.',
			institution: 'Sample University',
			image: '/webclip.png',
			link: '/authors',
		},
	],
}

function uniqueBooks(books: Book[]) {
	const seen = new Set<string>()
	return books.filter(book => {
		const key = (book.title || book.link || '').trim().toLowerCase()
		if (!key || seen.has(key)) return false
		seen.add(key)
		return true
	})
}

function normalizeAuthorPage(record: Record<string, any>, recordDate = '') {
	const authorId = record._id || record.id || ''
	const books = record._ssgBooks ?? []

	const articles = [...(record._previewArticles || record._ssgArticles || [])]
		.sort(
			(a: any, b: any) =>
				new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime(),
		)
		.map((article: any) => ({
			title: article.title || '',
			subtitle: article.teaser?.replace(/<[^>]+>/g, '') || '',
			image: article.image || '',
			link: article.slug ? `/article/${article.slug}` : article.link || '',
			authors: article.authors || [],
		})) as Article[]

	const authorRecords: Record<string, any>[] =
		record._previewAuthors ?? record._ssgBrowseAuthors ?? []
	const browseAuthors = authorRecords
		.filter(author => (author._id || author.id) !== authorId && !author.hidden)
		.slice(0, 5)
		.map(author => ({
			name: author.name || '',
			title: author.title || '',
			institution: author.institution || '',
			image: author.image || '',
			link: author.slug ? `/author/${author.slug}` : author.link || '',
		})) as Author[]

	return {
		profile: {
			name: record.name || '',
			title: record.institution || '',
			image: record.image || '',
			bio: record.bio || '',
			lastUpdated: record.lastUpdated || recordDate,
			books: uniqueBooks((record._previewBooks || books) as Book[]),
			amazonLink: record.amazonLink || '',
		},
		articles,
		browseAuthors,
	}
}

const boundRecord = useBoundPageItem<Record<string, any>>()

// The author's own record dates live outside the page context (which carries only
// content columns), so they are read from the collection by slug. The slug comes
// from the bound record — the author page is served by the CMS catch-all route and
// has no `:slug` param.
const authorSlug = computed(() => String(boundRecord.value?.slug ?? ''))
const needsDates = computed(() => !!boundRecord.value?._id && !boundRecord.value?.lastUpdated)
const { dates: authorDates } = useAuthorDates(authorSlug, needsDates)

const model = useBoundPageModel(
	record => normalizeAuthorPage(record, formatRecordDate(authorDates.value)),
	previewRecord,
)
const seoRecord = useBoundPageItem()
const route = useRoute()
useSeo(() => (seoRecord.value ? pageSeo(route.path, seoRecord.value) : null))
const profile = computed(
	() =>
		model.value?.profile ?? {
			name: '',
			title: '',
			image: '',
			bio: '',
			lastUpdated: '',
			books: [],
			amazonLink: '',
		},
)
const articles = computed<Article[]>(() => model.value?.articles ?? [])
const browseAuthors = computed<Author[]>(() => model.value?.browseAuthors ?? [])
</script>

<template>
	<div class="w1100px grid grid-wrap-3 gap-2 py-3 m_w-100p m_px-1 m_py-2">
		<div class="grid-span-2 m_grid-span-1">
			<BlockAuthorProfile :author-profile="profile" />

			<div v-if="articles && articles.length > 0">
				<h2
					class="txt30 regular mx-0 mt-0 line-height-12 balance pt-2 pb-0 m_txt22 m_line-height-13 m_pb-0 mb-0"
				>
					{{ articlesTitle }}
				</h2>
				<BlockArticleList
					:show-authors="'false'"
					:articles="articles"
					:show-topics="'false'"
					class="-ms-025 m_ms-0"
				/>
			</div>
		</div>

		<BlockAuthorList
			:author-list-data="{
				title: browseAuthorsTitle || 'Browse Authors',
				authors: browseAuthors,
				allAuthorsLink: allAuthorsLink || '/authors',
			}"
		/>
	</div>
</template>
